// src/app/core/chatPage/chatCopmonents/shared/TextGenerationViewModel.ts
import { makeObservable, observable, action, runInAction, computed } from 'mobx';
import { SingletonClass } from '@/app/utils/SingletonClass';
import { ChatController } from '@/app/data/controllers/ChatController';
import type { Message, MediaPart, ToolCall, ResultFile } from '.';
import { AIModel, AI_MODELS, ConversationResponseDTO, ConversationMessageResponseDTO } from './constants';
import { ChatApi } from '@/app/data/chat/api/ChatApi';
import { AgentSettingsViewModel } from '../chatInForm/components/agent/AgentSettingsViewModel';
import type { AgentRun } from '@kabco/omega-ws';
import { isMachineProgress } from '@kabco/omega-ws';
import { ProfileViewModel } from '@/app/core/tockensPaymentLogic/ProfileViewModel';

const SELECTED_MODELS_STORAGE_KEY = 'selected-model-by-type';
const TERMINAL_STATES = new Set<string>(['SETTLED', 'CANCELLING']);

interface MessageAgentStep {
    step: number;
    phase: string;
    reasoningText: string;
    reasoningActive: boolean;
    toolCallIds?: string[];
}

export class TextGenerationViewModel extends SingletonClass {
    private static _instance: TextGenerationViewModel;

    private chatController: ChatController;
    private chatApi: ChatApi = new ChatApi();

    messages: Message[] = [];
    showModelSelect = false;
    conversationId: string | null = null;
    activeConversationDbId: number | null = null;
    activeClientConversationId: string | null = null;

    draftText: string = '';
    attachedFiles: File[] = [];

    availableModels: AIModel[] = [];
    conversations: ConversationResponseDTO[] = [];

    isLoadingModels = false;
    isLoadingConversations = false;
    isLoadingMessages = false;
    hasMoreMessages = false;
    oldestLoadedSequence: number | null = null;

    isGenerating = false;
    activeRequestId: string | null = null;

    contextUsedTokens: number | null = null;
    contextWindowTokens: number | null = null;

    private messageHandlerUnsubscribe: (() => void) | null = null;
    private activeRun: AgentRun | null = null;

    activeModelType: 'text' | 'image' | 'video' = 'text';
    selectedModelIdByType: Record<string, string> = {};

    static getInstance(): TextGenerationViewModel {
        if (!TextGenerationViewModel._instance) {
            TextGenerationViewModel._instance = new TextGenerationViewModel();
        }
        return TextGenerationViewModel._instance;
    }

    constructor() {
        super();
        this.chatController = ChatController.getInstance();

        makeObservable(this, {
            messages: observable,
            showModelSelect: observable,
            conversationId: observable,
            activeConversationDbId: observable,
            activeClientConversationId: observable,
            draftText: observable,
            attachedFiles: observable,
            availableModels: observable,
            conversations: observable,
            isLoadingModels: observable,
            isLoadingConversations: observable,
            isLoadingMessages: observable,
            hasMoreMessages: observable,
            oldestLoadedSequence: observable,
            isGenerating: observable,
            activeRequestId: observable,
            activeModelType: observable,
            selectedModelIdByType: observable,
            contextUsedTokens: observable,
            contextWindowTokens: observable,

            selectedModel: computed,
            availableModelsByType: computed,

            loadConversations: action,
            loadConversationMessages: action,
            loadAvailableModels: action,
            handleSend: action,
            setDraftText: action,
            clearDraftText: action,
            addFiles: action,
            removeFile: action,
            clearFiles: action,
            stopGeneration: action,
            regenerateResponse: action,
            setSelectedModel: action,
            toggleModelSelect: action,
            startNewConversation: action,
            selectConversation: action,
            setActiveModelType: action,
            dispose: action,
            attachWebSocketHandler: action,
            detachWebSocketHandler: action,
            createNewConversation: action,
            deleteConversation: action,
            renameConversation: action,
            confirmResume: action,
            rejectResume: action,
            continueResume: action,
            resumeFromPause: action,
        });

        this.loadSelectedModels();
    }

    get availableModelsByType(): AIModel[] {
        const target = this.activeModelType === 'text' ? 'chat' : this.activeModelType;
        return this.availableModels.filter(model => model.channel === target);
    }

    get selectedModel(): AIModel {
        const models = this.availableModelsByType;
        if (models.length === 0) return AI_MODELS[0];
        const id = this.selectedModelIdByType[this.activeModelType];
        const found = models.find(m => m.id === id);
        return found || models[0] || AI_MODELS[0];
    }

    get textModel(): AIModel {
        const models = this.availableModels.filter(m => m.channel === 'chat');
        const id = this.selectedModelIdByType['text'];
        return models.find(m => m.id === id) || models[0] || AI_MODELS[0];
    }

    setActiveModelType(type: 'text' | 'image' | 'video') { this.activeModelType = type; }

    setSelectedModel(model: AIModel) {
        this.selectedModelIdByType[this.activeModelType] = model.id;
        this.persistSelectedModels();
    }

    setDraftText(text: string) { this.draftText = text; }
    clearDraftText() { this.draftText = ''; }
    clearFiles() { this.attachedFiles = []; }
    removeFile(index: number) { this.attachedFiles = this.attachedFiles.filter((_, i) => i !== index); }

    addFiles(files: File[]) {
        const validFiles = files.filter(file => {
            const isImage = file.type.startsWith('image/');
            if (isImage) {
                const isValidType = ['image/jpeg', 'image/png', 'image/gif'].includes(file.type);
                if (!isValidType) return false;
                const maxSize = 8 * 1024 * 1024;
                if (file.size > maxSize) return false;
            } else {
                const maxSize = 20 * 1024 * 1024; 
                if (file.size > maxSize) return false;
            }
            return true;
        });

        if (validFiles.length < files.length) {
            alert('Некоторые файлы не были добавлены: неподдерживаемый формат или превышен размер.');
        }

        this.attachedFiles = [...this.attachedFiles, ...validFiles];
    }

    private loadSelectedModels(): void {
        try {
            const saved = localStorage.getItem(SELECTED_MODELS_STORAGE_KEY);
            if (!saved) return;
            const parsed = JSON.parse(saved);
            if (parsed && typeof parsed === 'object') {
                this.selectedModelIdByType = { ...parsed };
            }
        } catch (e) {
            console.warn('[TextGenerationViewModel] loadSelectedModels failed', e);
        }
    }

    private persistSelectedModels(): void {
        try {
            localStorage.setItem(SELECTED_MODELS_STORAGE_KEY, JSON.stringify(this.selectedModelIdByType));
        } catch (e) {
            console.warn('[TextGenerationViewModel] persistSelectedModels failed', e);
        }
    }

    getTranslatedModelDescription(modelId: string, t: (key: string) => string): string {
        const descriptions: Record<string, string> = {
            'gpt-4.1': t('gpt41Description'),
            'gpt-4o': t('gpt4oDescription'),
            'gpt-5.1': t('claude3Description'),
            'gemini': t('geminiDescription'),
        };
        return descriptions[modelId] || this.selectedModel.description;
    }

    async loadConversations(): Promise<void> {
        this.isLoadingConversations = true;
        try {
            const conversations = await this.chatApi.getConversations();
            runInAction(() => {
                this.conversations = Array.isArray(conversations) ? conversations : [];
                this.isLoadingConversations = false;
            });
        } catch (error) {
            console.error('[TextGenerationViewModel] Ошибка загрузки conversations:', error);
            runInAction(() => {
                this.conversations = [];
                this.isLoadingConversations = false;
            });
        }
    }

    async loadConversationMessages(conversationId: number): Promise<void> {
        if (!conversationId) return;
        this.isLoadingMessages = true;
        try {
            const page = await this.chatApi.getConversationMessages(conversationId, 0, 50);
            const items = page?.items || [];
            items.sort((a, b) => a.sequenceNumber - b.sequenceNumber);

            const loadedMessages: Message[] = items.map((msg: any) => ({
                id: String(msg.id),
                text: msg.content || '',
                sender: msg.role === 'USER' ? 'user' : 'ai',
                timestamp: new Date(msg.createdAt),
                isWaiting: false,
                isStreaming: false,
                isStopped: false,
                error: undefined,
                modelId: msg.modelId || undefined,
                provider: msg.provider || undefined,
                userMessageId: msg.role === 'USER' ? msg.id : undefined,
                assistantMessageId: msg.role === 'ASSISTANT' ? msg.id : undefined,
                conversationId: conversationId,
                blocks: [],
                tools: (msg.tools || []).map((t: any) => ({
                    toolCallId: t.toolCallId,
                    toolName: t.toolName,
                    status: t.status || 'COMPLETED',
                    step: t.step,
                    lastProgress: t.lastProgress,
                    resultJson: t.resultJson,
                    argumentsJson: t.argumentsJson,
                })),
                mediaParts: (msg.parts || [])
                    .filter((part: any) => {
                        if (part.type !== 'MEDIA') return false;
                        const kind = (part.meta?.kind || '').toUpperCase();
                        return kind === 'IMAGE' || kind === 'VIDEO';
                    })
                    .map((part: any) => ({
                        mediaId: part.mediaId || part.id,
                        url: part.url,
                        kind: part.meta?.kind === 'VIDEO' ? 'video' : 'image',
                    })),
                files: (msg.parts || [])
                    .filter((part: any) => {
                        if (part.type !== 'MEDIA') return false;
                        const kind = (part.meta?.kind || '').toUpperCase();
                        return kind === 'FILE';
                    })
                    .map((part: any) => ({
                        name: part.fileName || part.url.split('/').pop() || 'file',
                        path: part.meta?.path || part.url
                    })),
                agentSteps: (msg.steps || []).map((s: any) => ({
                    step: s.stepIndex || s.step || 1,
                    phase: s.phase || 'calling_llm',
                    reasoningText: s.reasoningText || '',
                    reasoningActive: false,
                })),
                mediaPhase: null,
                machinePhase: null,
                attachments: (msg.parts || [])
                    .filter((part: any) => {
                        if (part.type !== 'MEDIA') return false;
                        const kind = (part.meta?.kind || '').toUpperCase();
                        return kind === 'IMAGE' || kind === 'VIDEO' || kind === 'FILE';
                    })
                    .map((part: any) => ({
                        url: part.url,
                        kind: part.meta?.kind === 'VIDEO' ? 'video' : part.meta?.kind === 'FILE' ? 'file' : 'image',
                        name: part.fileName || 'file'
                    }))
            }));

            let lastUserMessageId: number | null = null;
            for (const msg of loadedMessages) {
                if (msg.sender === 'user') {
                    lastUserMessageId = msg.userMessageId ?? null;
                    msg.conversationId = conversationId;
                } else {
                    msg.userMessageId = lastUserMessageId;
                    msg.conversationId = conversationId;
                }
            }

            runInAction(() => {
                this.messages = loadedMessages;
                this.isLoadingMessages = false;
                this.hasMoreMessages = page.hasMore;
                if (items.length > 0) {
                    this.oldestLoadedSequence = items[0].sequenceNumber;
                } else {
                    this.oldestLoadedSequence = null;
                }
                this.updateContextFromHistory(items);
            });
        } catch (error) {
            console.error('[TextGenerationViewModel] Ошибка загрузки сообщений:', error);
            runInAction(() => {
                this.messages = [];
                this.isLoadingMessages = false;
                this.hasMoreMessages = false;
                this.oldestLoadedSequence = null;
            });
        }
    }

    async loadAvailableModels(): Promise<void> {
        this.isLoadingModels = true;
        try {
            const models = await this.chatApi.getAvailableModels();
            const validModels = models.filter(m => m.id && m.name);
            runInAction(() => {
                this.availableModels = validModels;

                const setDefault = (type: 'text' | 'image' | 'video', channel: 'chat' | 'image' | 'video') => {
                    if (!this.selectedModelIdByType[type]) {
                        const found = validModels.find(m => m.channel === channel);
                        if (found) this.selectedModelIdByType[type] = found.id;
                    }
                };
                setDefault('text', 'chat');
                setDefault('image', 'image');
                setDefault('video', 'video');

                const profile = ProfileViewModel.getInstance().profile;
                const pickDefault = (type: 'text' | 'image' | 'video', modelId?: string) => {
                    if (modelId && validModels.some(m => m.id === modelId)) {
                        this.selectedModelIdByType[type] = modelId;
                    }
                };
                pickDefault('text', profile?.defaultModel);
                pickDefault('image', profile?.defaultImageModel);
                pickDefault('video', profile?.defaultVideoModel);

                this.persistSelectedModels();
                this.isLoadingModels = false;
            });
        } catch (error) {
            console.error('[TextGenerationViewModel] Ошибка загрузки моделей:', error);
            runInAction(() => {
                this.availableModels = [];
                this.isLoadingModels = false;
            });
        }
    }

    attachWebSocketHandler(): void {
        if (this.messageHandlerUnsubscribe) return;
        this.messageHandlerUnsubscribe = this.chatController.onMessage((event) => {
            this.handleWebSocketEvent(event);
        });
    }

    private handleWebSocketEvent(event: any): void {
        if (event.type === 'reconnected') {
            if (this.activeConversationDbId != null) {
                void this.restoreActiveRun(this.activeConversationDbId);
            }
            return;
        }

        if (event.type === 'runs.list') {
            void this.handleRecoverableRuns(event.runs);
            return;
        }

        if (event.type === 'serverError') {
            if (import.meta.env.DEV) console.warn('[VM] serverError', event.code, event.message, event.requestId);
            return;
        }

        if (event.type === 'network') {
            if (import.meta.env.DEV) console.log('[VM] network', event.online);
            return;
        }

        if (event.type === 'conversation.updated') {
            const sendRequestId = this.activeRun?.sendRequestId;
            if (sendRequestId && event.requestId === sendRequestId) {
                runInAction(() => {
                    if (event.conversationId) {
                        this.activeConversationDbId = event.conversationId;
                    }
                    if (event.conversationAiId) {
                        this.conversationId = event.conversationAiId;
                    }
                    this.activeClientConversationId = null;

                    if (event.conversationId && Array.isArray(this.conversations) && !this.conversations.some(c => c.id === event.conversationId)) {
                        const newConv: ConversationResponseDTO = {
                            id: event.conversationId,
                            userId: 0,
                            telegramId: 0,
                            conversationAiId: event.conversationAiId || '',
                            title: event.title || 'Новый чат',
                            provider: this.selectedModel.provider || 'OPENAI',
                            isActive: true,
                            isDeleted: false,
                            createdAt: new Date().toISOString(),
                            lastUsedAt: new Date().toISOString(),
                        };
                        if (!Array.isArray(this.conversations)) {
                            this.conversations = [];
                        }
                        this.conversations = [newConv, ...this.conversations];
                    }
                });
            }
        } else if (event.type === 'user.message.created') {
            const clientMessageId = event.clientMessageId;
            const userMessage = this.messages.find(m => m.id === clientMessageId && m.sender === 'user');
            if (userMessage) {
                userMessage.userMessageId = event.userMessageId;
                userMessage.conversationId = event.conversationId;
            }
            const aiMessage = this.messages.find(m => m.id === `ai-${clientMessageId}`);
            if (aiMessage) {
                aiMessage.userMessageId = event.userMessageId;
                aiMessage.conversationId = event.conversationId;
            }
        } else if (event.type === 'assistant.message.started') {
            const runId = event.requestId;
            const aiMessage = this.messages.find(m => m.id === `ai-${runId}`);
            if (aiMessage) {
                aiMessage.assistantMessageId = event.messageId;
                aiMessage.conversationId = event.conversationId;
            }
        }
    }

    private async handleRecoverableRuns(runs: any[]): Promise<void> {
        if (!Array.isArray(runs) || runs.length === 0) return;
        if (this.activeConversationDbId != null) return;
        if (this.isGenerating) return;
        if (this.messages.length > 0) return;

        const target = runs.find(r => typeof r?.conversationId === 'number' && r.conversationId > 0);
        if (!target) return;
        const conversationId = target.conversationId as number;

        if (!Array.isArray(this.conversations) || this.conversations.length === 0) {
            await this.loadConversations();
        }

        const conversation = this.conversations.find(c => c.id === conversationId);
        if (!conversation) return;

        this.selectConversation(conversation);
    }

    detachWebSocketHandler(): void {
        if (this.messageHandlerUnsubscribe) {
            this.messageHandlerUnsubscribe();
            this.messageHandlerUnsubscribe = null;
        }
    }

    handleSend(text: string): void {
        if (!text.trim() && this.attachedFiles.length === 0) return;
        this.sendPrompt(text.trim());
        this.clearDraftText();
        this.clearFiles();
    }

    stopGeneration(): void {
        if (this.activeRequestId) {
            this.chatController.cancel(this.activeRequestId);
        }
    }

    confirmResume(_messageId: string, toolCallId: string, argumentsJson?: string): void {
        this.chatController.resume({ action: 'CONFIRM', toolCallId, argumentsJson });
    }

    rejectResume(_messageId: string, toolCallId: string): void {
        this.chatController.resume({ action: 'REJECT', toolCallId });
    }

    continueResume(_messageId: string): void {
        this.chatController.resume({ action: 'CONTINUE' });
    }

    resumeFromPause(
        _messageId: string,
        action: 'CONFIRM' | 'REJECT' | 'ANSWER' | 'CONTINUE',
        opts?: { toolCallId?: string; argumentsJson?: string; answer?: string },
    ): void {
        this.chatController.resume({ action, ...opts });
    }

    regenerateResponse(aiMessageId: string): void {
        const aiMessage = this.messages.find(m => m.id === aiMessageId);
        if (!aiMessage || aiMessage.sender !== 'ai') return;
        if (aiMessage.userMessageId == null || aiMessage.conversationId == null) {
            console.warn('[TextGenerationViewModel] Недостаточно данных для регенерации', aiMessage);
            return;
        }

        if (this.isGenerating) {
            this.stopGeneration();
        }

        runInAction(() => {
            aiMessage.text = '';
            aiMessage.isWaiting = true;
            aiMessage.isStreaming = false;
            aiMessage.isStopped = false;
            aiMessage.error = undefined;
            aiMessage.tokensUsed = undefined;
            aiMessage.blocks = [];
            aiMessage.tools = [];
            aiMessage.mediaParts = [];
            aiMessage.agentSteps = [];
            aiMessage.isPaused = false;
            aiMessage.lastPaused = null;
            aiMessage.state = undefined;
            aiMessage.mediaPhase = null;
            aiMessage.machinePhase = null;
            aiMessage.plan = undefined;
            aiMessage.files = undefined;
        });

        const model = this.selectedModel.id;
        const run = this.chatController.retry(aiMessage.userMessageId!, aiMessage.conversationId!, model);

        this.activeRun = run;
        this.activeRequestId = run.requestId;
        this.isGenerating = true;

        this.setupRunListeners(run, aiMessageId, false);

        run.completed()
            .then((completionResult: any) => {
                runInAction(() => {
                    const msg = this.messages.find(m => m.id === aiMessageId);
                    if (msg) {
                        if (completionResult?.appTokens != null) {
                            msg.tokensUsed = completionResult.appTokens;
                        }
                        msg.isStreaming = false;
                        msg.isWaiting = false;
                        msg.isStopped = false;
                        msg.phase = undefined;
                        msg.mediaPhase = null;
                        msg.machinePhase = null;
                        msg.step = undefined;
                        msg.state = 'SETTLED';
                        msg.isPaused = false;
                        this.applyPipelineSnapshot(run, msg);
                        this.finalizeReasoning(msg);
                        this.applyRunFiles(run, msg);
                    }
                    this.isGenerating = false;
                    this.activeRequestId = null;
                    this.activeRun = null;
                });
            })
            .catch(() => {})
            .finally(() => {
                run.dispose();
            });
    }

    private updateContextUsage(event: any): void {
        if (event.type === 'context.assembled') {
            this.contextUsedTokens = event.usedTokens;
            this.contextWindowTokens = event.contextWindowTokens;
        }
    }

    async restoreActiveRun(conversationId: number): Promise<void> {
        if (typeof conversationId !== 'number' || !Number.isFinite(conversationId) || conversationId <= 0) {
            return;
        }
        if (this.isGenerating) return;
        try {
            const run = await this.chatController.openRun(conversationId);
            if (!run) return;
            const messageId = `ai-${run.requestId}`;
            if (this.messages.some(m => m.id === messageId)) return;

            runInAction(() => {
                this.messages.push({
                    id: messageId,
                    text: run.text,
                    sender: 'ai',
                    timestamp: new Date(),
                    isWaiting: false,
                    isStreaming: !run.isSettled,
                    isStopped: false,
                    error: undefined,
                    modelId: this.textModel.id,
                    provider: this.textModel.provider || null,
                    tokensUsed: undefined,
                    userMessageId: run.userMessageId,
                    assistantMessageId: run.assistantMessageId,
                    conversationId: conversationId,
                    blocks: run.blocks || [],
                    tools: (run.getPipeline().tools as any) || [],
                    mediaParts: [],
                    agentSteps: [],
                    state: run.state,
                    isPaused: Boolean(run.isPaused),
                    mediaPhase: null,
                    machinePhase: null,
                    lastPaused: run.lastPaused
                        ? {
                            kind: (run.lastPaused as any).kind,
                            toolCallId: (run.lastPaused as any).toolCallId,
                            toolName: (run.lastPaused as any).toolName,
                            argumentsJson: (run.lastPaused as any).argumentsJson,
                            prompt: (run.lastPaused as any).prompt ?? null,
                            step: (run.lastPaused as any).step ?? null,
                            epoch: (run.lastPaused as any).epoch ?? null,
                        }
                        : null,
                });
                this.activeRun = run;
                this.activeRequestId = run.requestId;
                this.isGenerating = !run.isSettled;
            });

            this.setupRunListeners(run, messageId, false);
            this.applyPipelineSnapshot(run, this.messages.find(m => m.id === messageId)!);
            this.applyRunFiles(run, this.messages.find(m => m.id === messageId)!);

            if (!run.isSettled) {
                run.completed()
                    .then((completionResult: any) => {
                        runInAction(() => {
                            const msg = this.messages.find(m => m.id === messageId);
                            if (msg) {
                                if (completionResult?.appTokens != null) {
                                    msg.tokensUsed = completionResult.appTokens;
                                }
                                msg.isStreaming = false;
                                msg.isWaiting = false;
                                msg.isStopped = false;
                                msg.phase = undefined;
                                msg.mediaPhase = null;
                                msg.machinePhase = null;
                                msg.step = undefined;
                                msg.state = 'SETTLED';
                                msg.isPaused = false;
                                this.applyPipelineSnapshot(run, msg);
                                this.finalizeReasoning(msg);
                                this.applyRunFiles(run, msg);
                            }
                            if (this.activeRequestId === run.requestId) {
                                this.isGenerating = false;
                                this.activeRequestId = null;
                                this.activeRun = null;
                            }
                        });
                    })
                    .catch(() => {});
            }
        } catch (e: any) {
            const code = e?.code || e?.original?.code;
            if (code === 'RUN_NOT_FOUND' || code === 'VALIDATION_ERROR') return;
            if (code === 'FORBIDDEN') {
                console.warn('[TextGenerationViewModel] restoreActiveRun: FORBIDDEN');
                return;
            }
            console.warn('[TextGenerationViewModel] restoreActiveRun failed', e);
        }
    }

    private updateContextFromHistory(items: ConversationMessageResponseDTO[]): void {
        const totalTokens = items.reduce((sum, msg) => sum + (msg.totalTokens || 0), 0);
        this.contextUsedTokens = totalTokens;
        this.contextWindowTokens = this.selectedModel.contextWindowTokens || 128000;
    }

    private async sendPrompt(
        text: string,
        options?: { messageId?: string; appendUserMessage?: boolean }
    ): Promise<void> {
        if (!this.chatController) return;
        const shouldAppendUserMessage = options?.appendUserMessage ?? true;
        const localMessageId = options?.messageId ?? `msg-${Date.now()}`;

        const settings = AgentSettingsViewModel.getInstance();
        
        const filesToSend = [...this.attachedFiles];
        
        let conversationId: number | null = null;
        let clientConversationId: string | null = null;

        if (this.activeConversationDbId != null) {
            conversationId = this.activeConversationDbId;
        } else {
            clientConversationId = this.activeClientConversationId || this.generateClientConversationId();
            runInAction(() => {
                this.activeClientConversationId = clientConversationId;
            });
        }

        const localAttachments = filesToSend.map(file => ({
            url: URL.createObjectURL(file),
            kind: file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'file',
            name: file.name
        } as const));

        const fallbackText = text.trim() ? text : (filesToSend.length > 0 ? '[Файл]' : '');

        runInAction(() => {
            if (shouldAppendUserMessage) {
                this.messages.push({
                    id: localMessageId,
                    text: fallbackText,
                    sender: 'user',
                    timestamp: new Date(),
                    userMessageId: undefined,
                    conversationId: undefined,
                    attachments: localAttachments.length > 0 ? localAttachments : undefined,
                });
            }
            this.messages = this.messages.filter(m => m.id !== `ai-${localMessageId}`);
            this.messages.push({
                id: `ai-${localMessageId}`,
                text: '',
                sender: 'ai',
                timestamp: new Date(),
                isWaiting: true,
                isStreaming: false,
                isStopped: false,
                error: undefined,
                modelId: this.selectedModel.id,
                provider: this.selectedModel.provider || null,
                tokensUsed: undefined,
                userMessageId: undefined,
                assistantMessageId: undefined,
                conversationId: undefined,
                blocks: [],
                tools: [],
                mediaParts: [],
                agentSteps: [],
                mediaPhase: null,
                machinePhase: null,
            });
            this.isGenerating = true;
            this.activeRequestId = null;
        });

        let uploadedAttachments: any[] = [];
        if (filesToSend.length > 0) {
            try {
                uploadedAttachments = await Promise.all(filesToSend.map(file => 
                    this.chatController.uploadFile({
                        file,
                        fileName: file.name,
                        conversationId: conversationId ?? undefined,
                        clientConversationId: clientConversationId ?? undefined
                    })
                ));
            } catch (e: any) {
                console.error('Upload failed', e);
                runInAction(() => {
                    const msg = this.messages.find(m => m.id === `ai-${localMessageId}`);
                    if (msg) {
                        msg.error = `Не удалось загрузить файлы: ${e.message || 'Ошибка сервера'}`;
                        msg.isWaiting = false;
                        msg.isStreaming = false;
                    }
                    this.isGenerating = false;
                    this.activeRequestId = null;
                    this.activeRun = null;
                });
                return;
            }
        }

        const finalText = text + settings.getPromptSuffix();

        const run = this.chatController.sendText(
            finalText,
            conversationId,
            clientConversationId,
            this.textModel.id,
            localMessageId,
            uploadedAttachments
        );

        this.activeRun = run;
        this.activeRequestId = run.requestId;
        this.isGenerating = true;

        this.setupRunListeners(run, localMessageId, true);

        try {
            const completionResult: any = await run.completed();

            runInAction(() => {
                const msg = this.messages.find(m => m.id === `ai-${localMessageId}`);
                if (msg) {
                    if (completionResult?.appTokens != null) {
                        msg.tokensUsed = completionResult.appTokens;
                    }
                    msg.isStreaming = false;
                    msg.isWaiting = false;
                    msg.isStopped = false;
                    msg.phase = undefined;
                    msg.mediaPhase = null;
                    msg.machinePhase = null;
                    msg.step = undefined;
                    msg.state = 'SETTLED';
                    msg.isPaused = false;
                    this.applyPipelineSnapshot(run, msg);
                    this.finalizeReasoning(msg);
                    this.applyRunFiles(run, msg);
                }
                this.isGenerating = false;
                this.activeRequestId = null;
                this.activeRun = null;
            });
        } catch (e) {
            // отмена/ошибка уже обработаны
        } finally {
            run.dispose();
        }
    }

    private setupRunListeners(run: AgentRun, messageId: string, isSend: boolean): void {
        const msgId = isSend ? `ai-${messageId}` : messageId;

        runInAction(() => {
            const msg = this.messages.find(m => m.id === msgId);
            if (msg) this.applyPipelineSnapshot(run, msg);
        });

        run.on('chunk', () => {
            runInAction(() => {
                const msg = this.messages.find(m => m.id === msgId);
                if (msg) {
                    msg.text = run.text;
                    msg.blocks = run.blocks;
                    msg.isStreaming = true;
                    msg.isWaiting = false;
                    this.finalizeReasoning(msg);
                }
            });
        });

        run.on('assistantStarted', () => {
            runInAction(() => {
                const msg = this.messages.find(m => m.id === msgId);
                if (msg) {
                    msg.isWaiting = false;
                    msg.isStreaming = true;
                    this.finalizeReasoning(msg);
                }
            });
        });

        run.on('progress', (event: any) => {
            runInAction(() => {
                const msg = this.messages.find(m => m.id === msgId);
                if (msg) {
                    if (isMachineProgress(event)) {
                        msg.machinePhase = event.phase;
                    } else if (event.channel === 'CHAT' || !event.channel) {
                        msg.phase = event.phase;
                    } else {
                        msg.mediaPhase = event.phase;
                    }
                    msg.step = typeof event.step === 'number' ? event.step : undefined;
                    msg.isWaiting = false;
                    msg.isStreaming = true;
                    this.updateAgentStep(msg, event);
                }
            });
        });

        run.on('reasoning', (event: any) => {
            runInAction(() => {
                const msg = this.messages.find(m => m.id === msgId);
                if (msg) this.updateAgentReasoning(msg, event);
            });
        });

        run.on('toolProgress', (event: any) => {
            runInAction(() => {
                const msg = this.messages.find(m => m.id === msgId);
                if (msg) this.applyToolProgress(msg, event);
            });
        });

        run.on('pipelineChanged', () => {
            runInAction(() => {
                const msg = this.messages.find(m => m.id === msgId);
                if (msg) this.applyPipelineSnapshot(run, msg);
            });
        });

        run.on('planChanged', () => {
            runInAction(() => {
                const msg = this.messages.find(m => m.id === msgId);
                if (msg && run.plan) {
                    msg.plan = run.plan.map((p: any) => ({
                        title: p.text || p.title || p.content || 'Шаг',
                        status: p.status || 'pending'
                    }));
                }
            });
        });

        run.on('media', (event: any) => {
            runInAction(() => {
                const msg = this.messages.find(m => m.id === msgId);
                if (!msg) return;

                const kind = (event.meta?.kind || '').toUpperCase();
                if (kind === 'IMAGE' || kind === 'VIDEO') {
                    const already = msg.mediaParts?.some(m => {
                        if (event.mediaId && m.mediaId) return m.mediaId === event.mediaId;
                        return m.url === event.url;
                    });
                    if (!already) {
                        if (!msg.mediaParts) msg.mediaParts = [];
                        msg.mediaParts.push({
                            mediaId: event.mediaId,
                            url: event.url,
                            kind: kind === 'VIDEO' ? 'video' : 'image',
                        });
                    }
                } else if (kind === 'FILE') {
                    const name = event.fileName || event.url.split('/').pop() || 'file';
                    const path = event.meta?.path || event.url;
                    if (!msg.files) msg.files = [];
                    if (!msg.files.some(f => f.path === path)) {
                        msg.files.push({ name, path });
                    }
                }
                
                msg.mediaPhase = null;
                this.applyPipelineSnapshot(run, msg);
            });
        });

        run.on('error', (errorEvent: any) => {
            runInAction(() => {
                const msg = this.messages.find(m => m.id === msgId);
                if (msg) {
                    msg.error = errorEvent.message || 'Ошибка генерации';
                    msg.isStreaming = false;
                    msg.isWaiting = false;
                    msg.mediaPhase = null;
                    msg.machinePhase = null;
                    msg.tokensUsed = errorEvent.appTokens ?? null;
                    msg.state = 'SETTLED';
                    msg.isPaused = false;
                    this.applyPipelineSnapshot(run, msg);
                    this.finalizeReasoning(msg);
                    this.applyRunFiles(run, msg);
                }
                this.isGenerating = false;
                this.activeRequestId = null;
                this.activeRun = null;
            });
        });

        run.on('cancelled', () => {
            runInAction(() => {
                const msg = this.messages.find(m => m.id === msgId);
                if (msg) {
                    msg.isStreaming = false;
                    msg.isWaiting = false;
                    msg.isStopped = true;
                    msg.mediaPhase = null;
                    msg.machinePhase = null;
                    msg.state = 'SETTLED';
                    msg.isPaused = false;
                    this.applyPipelineSnapshot(run, msg);
                    this.finalizeReasoning(msg);
                    this.applyRunFiles(run, msg);
                }
                this.isGenerating = false;
                this.activeRequestId = null;
                this.activeRun = null;
            });
        });

        run.on('event', (event: any) => {
            if (event.type === 'context.assembled') {
                this.updateContextUsage(event);
                runInAction(() => {
                    const msg = this.messages.find(m => m.id === msgId);
                    if (msg) msg.tokensUsed = event.usedTokens;
                });
            }
        });

        run.on('stateChanged', (payload: any) => {
            const nextState =
                typeof payload === 'string'
                    ? payload
                    : (payload?.state ?? payload?.next ?? payload?.to ?? '');
            runInAction(() => {
                const msg = this.messages.find(m => m.id === msgId);
                if (!msg) return;
                msg.state = nextState || msg.state;
                msg.isPaused = nextState === 'PAUSED';

                if (nextState === 'PAUSED' && !msg.lastPaused && run.lastPaused) {
                    msg.lastPaused = {
                        kind: (run.lastPaused as any).kind,
                        toolCallId: (run.lastPaused as any).toolCallId,
                        toolName: (run.lastPaused as any).toolName,
                        argumentsJson: (run.lastPaused as any).argumentsJson,
                        prompt: (run.lastPaused as any).prompt ?? null,
                        step: (run.lastPaused as any).step ?? null,
                        epoch: (run.lastPaused as any).epoch ?? null,
                    };
                }

                if (TERMINAL_STATES.has(nextState)) {
                    msg.mediaPhase = null;
                    msg.machinePhase = null;
                    this.applyPipelineSnapshot(run, msg);
                    this.finalizeReasoning(msg);
                    this.applyRunFiles(run, msg);
                }
            });
        });

        run.on('paused', (event: any) => {
            runInAction(() => {
                const msg = this.messages.find(m => m.id === msgId);
                if (msg) {
                    msg.state = 'PAUSED';
                    msg.isPaused = true;
                    msg.lastPaused = {
                        kind: event.kind,
                        toolCallId: event.toolCallId,
                        toolName: event.toolName,
                        argumentsJson: event.argumentsJson,
                        prompt: event.prompt ?? null,
                        step: event.step ?? null,
                        epoch: event.epoch ?? null,
                    };
                    this.applyPipelineSnapshot(run, msg);
                    this.finalizeReasoning(msg);
                }
            });
        });
    }

    private applyRunFiles(run: AgentRun, msg: Message) {
        if (run.files && run.files.length > 0) {
            msg.files = run.files;
        }
    }

    private finalizeReasoning(msg: Message): void {
        if (!msg.agentSteps || msg.agentSteps.length === 0) return;
        for (const step of msg.agentSteps) {
            step.reasoningActive = false;
        }
    }

    private computeReasoningActive(step: any): boolean {
        if (typeof step?.reasoningEndedAtEpochMs !== 'undefined') {
            return step.reasoningEndedAtEpochMs === null;
        }
        const spans = Array.isArray(step?.phaseSpans) ? step.phaseSpans : [];
        if (spans.length === 0) return false;
        return spans.some((span: any) =>
            span?.endedAtEpochMs === null && span?.phase === 'calling_llm'
        );
    }

    private applyPipelineSnapshot(run: AgentRun, msg: Message): void {
        const pipeline: any = run.getPipeline?.();
        if (!pipeline) return;

        const settled = Boolean((run as any).isSettled) || msg.state === 'SETTLED';
        const paused = Boolean(msg.isPaused);
        const freezeReasoning = settled || paused;

        if (run.blocks) msg.blocks = run.blocks;

        if (Array.isArray(pipeline.steps)) {
            const prevByStep = new Map<number, MessageAgentStep>();
            for (const s of (msg.agentSteps ?? []) as MessageAgentStep[]) {
                prevByStep.set(s.step, s);
            }

            const merged: MessageAgentStep[] = pipeline.steps.map((step: any) => {
                const prev = prevByStep.get(step.step);
                const fromPipeline = (step.reasoningText || '').trim();
                const reasoningText = fromPipeline.length > 0
                    ? step.reasoningText
                    : (prev?.reasoningText || '');

                return {
                    step: step.step,
                    phase: step.phase,
                    reasoningText,
                    reasoningActive: freezeReasoning ? false : this.computeReasoningActive(step),
                    toolCallIds: step.toolCallIds || prev?.toolCallIds || [],
                };
            });

            const pipelineSteps = new Set(merged.map(s => s.step));
            for (const s of (msg.agentSteps ?? []) as MessageAgentStep[]) {
                if (!pipelineSteps.has(s.step)) merged.push(s);
            }
            merged.sort((a, b) => a.step - b.step);

            msg.agentSteps = merged;

            const stepMedia: MediaPart[] = [];
            const stepFiles: ResultFile[] = [];
            
            for (const step of pipeline.steps) {
                const parts = Array.isArray(step.mediaParts) ? step.mediaParts : [];
                for (const p of parts) {
                    if (!p?.url) continue;
                    const kind = (p.kind || p.meta?.kind || '').toUpperCase();
                    if (kind === 'IMAGE' || kind === 'VIDEO') {
                        stepMedia.push({
                            mediaId: p.mediaId ?? p.id,
                            url: p.url,
                            kind: kind === 'VIDEO' ? 'video' : 'image',
                        });
                    } else if (kind === 'FILE') {
                        const name = p.meta?.fileName || p.url.split('/').pop() || 'file';
                        const path = p.meta?.path || p.url;
                        stepFiles.push({ name, path });
                    }
                }
            }
            
            if (stepMedia.length > 0) {
                const current = msg.mediaParts ?? [];
                const out = [...current];
                for (const m of stepMedia) {
                    const dup = out.some(x => {
                        if (m.mediaId && x.mediaId) return x.mediaId === m.mediaId;
                        return x.url === m.url;
                    });
                    if (!dup) out.push(m);
                }
                msg.mediaParts = out;
            }

            if (stepFiles.length > 0) {
                if (!msg.files) msg.files = [];
                for (const f of stepFiles) {
                    if (!msg.files.some(x => x.path === f.path)) {
                        msg.files.push(f);
                    }
                }
            }
        }

        if (Array.isArray(pipeline.tools)) {
            msg.tools = pipeline.tools.map((tool: any) => ({
                toolCallId: tool.toolCallId,
                toolName: tool.toolName,
                status: tool.status,
                step: typeof tool.step === 'number' ? tool.step : undefined,
                lastProgress: tool.lastProgress,
                resultJson: tool.resultJson,
                errorCode: tool.errorCode,
                errorMessage: tool.errorMessage,
                argumentsJson: tool.argumentsJson ?? null,
            }));
        }
    }

    private applyToolProgress(msg: Message, event: any): void {
        if (!event?.toolCallId) return;
        if (!msg.tools) msg.tools = [];

        const existing = msg.tools.find(t => t.toolCallId === event.toolCallId);

        const tool: ToolCall = existing ?? {
            toolCallId: event.toolCallId,
            toolName: event.toolName || 'tool',
            status: event.status || 'RUNNING',
            step: typeof event.step === 'number' ? event.step : undefined,
            lastProgress: undefined,
            resultJson: null,
            errorCode: null,
            errorMessage: null,
            argumentsJson: null,
        };

        if (!existing) {
            msg.tools.push(tool);
        } else {
            if (event.toolName) tool.toolName = event.toolName;
            if (event.status) tool.status = event.status;
            if (typeof event.step === 'number') tool.step = event.step;
        }

        if (event.kind || event.phase || event.payload != null) {
            tool.lastProgress = {
                kind: event.kind || tool.lastProgress?.kind || 'progress',
                phase: event.phase || tool.lastProgress?.phase || '',
                payload: event.payload ?? tool.lastProgress?.payload,
            };
        }
        if (event.resultJson != null) tool.resultJson = event.resultJson;
        if (event.code) tool.errorCode = event.code;
        if (event.message) tool.errorMessage = event.message;
    }

    private generateClientConversationId(): string {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        return `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    private updateAgentStep(msg: Message, event: any): void {
        if (!msg.agentSteps) msg.agentSteps = [];
        let step = msg.agentSteps.find(s => s.step === event.step);
        if (!step) {
            step = { step: event.step, phase: event.phase, reasoningText: '', reasoningActive: false };
            msg.agentSteps.push(step);
        } else {
            step.phase = event.phase;
        }
    }

    private updateAgentReasoning(msg: Message, event: any): void {
        if (!msg.agentSteps) msg.agentSteps = [];
        let step = msg.agentSteps.find(s => s.step === event.step);
        if (!step) {
            step = { step: event.step, phase: 'calling_llm', reasoningText: '', reasoningActive: true };
            msg.agentSteps.push(step);
        } else {
            step.reasoningText += event.text;
            step.reasoningActive = true;
        }
    }

    toggleModelSelect(): void { this.showModelSelect = !this.showModelSelect; }

    startNewConversation(): void {
        this.stopGeneration();
        runInAction(() => {
            this.conversationId = null;
            this.activeConversationDbId = null;
            this.activeClientConversationId = null;
            this.messages = [];
            this.isGenerating = false;
            this.activeRequestId = null;
            this.activeRun = null;
            this.isLoadingMessages = false;
            this.hasMoreMessages = false;
            this.oldestLoadedSequence = null;
            this.contextUsedTokens = null;
            this.contextWindowTokens = null;
        });
    }

    selectConversation(conversation: ConversationResponseDTO): void {
        const convId = Number(conversation?.id);
        if (!Number.isFinite(convId) || convId <= 0) {
            console.warn('[TextGenerationViewModel] invalid conversation.id', conversation);
            return;
        }
        this.stopGeneration();
        runInAction(() => {
            this.conversationId = conversation.conversationAiId;
            this.activeConversationDbId = conversation.id;
            this.activeClientConversationId = null;
            this.messages = [];
            this.isGenerating = false;
            this.activeRequestId = null;
            this.activeRun = null;
            this.contextUsedTokens = null;
            this.contextWindowTokens = null;
        });
        this.loadConversationMessages(conversation.id);
        void this.restoreActiveRun(conversation.id);
    }

    async createNewConversation(): Promise<void> {
        try {
            const provider = this.selectedModel.provider || 'OPENAI';
            const title = 'Новый чат';
            const created = await this.chatApi.createConversation(title, provider);
            runInAction(() => {
                if (!Array.isArray(this.conversations)) {
                    this.conversations = [];
                }
                this.conversations = [created, ...this.conversations];
                this.selectConversation(created);
            });
        } catch (error) {
            console.error('[TextGenerationViewModel] Ошибка создания диалога:', error);
        }
    }

    async deleteConversation(conversationId: number): Promise<void> {
        try {
            await this.chatApi.deleteConversation(conversationId);
            runInAction(() => {
                const wasActive = this.activeConversationDbId === conversationId;
                this.conversations = this.conversations.filter(c => c.id !== conversationId);
                if (wasActive) {
                    this.startNewConversation();
                }
            });
        } catch (error) {
            console.error('[TextGenerationViewModel] Ошибка удаления диалога:', error);
        }
    }

    async renameConversation(conversationId: number, newTitle: string): Promise<void> {
        try {
            const updated = await this.chatApi.updateConversationTitle(conversationId, newTitle);
            runInAction(() => {
                if (!Array.isArray(this.conversations)) {
                    this.conversations = [];
                }
                this.conversations = this.conversations.map(c => c.id === conversationId ? updated : c);
            });
        } catch (error) {
            console.error('[TextGenerationViewModel] Ошибка переименования диалога:', error);
        }
    }

    dispose(): void {
        this.detachWebSocketHandler();
    }
}